import React, { useEffect, useState } from "react";
import {
  Box, Typography, Button, TextField, Drawer, IconButton, CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import moment from "moment";
import { useSnackbar } from "notistack";
import { get_my_slots_api, propose_session_api, flag_weak_area_api } from "../../../DAL/Tutors/Tutors";
import { create_student_task_api, add_record_note_api, message_student_api } from "../../../DAL/SchoolPortal/SchoolPortal";
import {
  border, textPrimary, textSecondary, textMuted, accent, font,
  warning, error as errorColor, easing, inputSx, primaryButtonSx, secondaryButtonSx,
  formatSubjectName, subjectsForRegion,
} from "../../../layouts/schoolPortal/schoolPortalTheme";
import { label as labelSx, meta as metaSx } from "./recordTheme";
import { useSchool } from "../../../Hooks/SchoolContext";
import { regionOf, anInstructor, formatMoney } from "../../../utils/region";

/**
 * The four actions you take FROM a record, in a panel rather than a page.
 *
 * Navigating away to act loses the context you opened the record to get — the
 * weak lesson, the risk reason. All four therefore slide over
 * the record and leave it visible underneath.
 *
 * THE NOTE TRAVELS WITH THE BOOKING
 * The CFI's reason — "W&B arithmetic, not the concept; he picks right answers
 * with wrong working" — is written once here and is what the student is shown,
 * what seeds the pre-session agenda, and what appears on the timeline. The
 * propose endpoint rejects an empty note for that reason.
 */

const DURATIONS = [30, 60];

/**
 * A whole number of pounds is written as "£25", not "£25.00". The panel prints
 * this figure three times; two decimal places on all of them turns a price
 * into an invoice.
 */
const money = (amount, region) =>
  formatMoney(amount, region, { decimals: Number.isInteger(Number(amount)) ? 0 : 2 });

/**
 * The default reason on a proposed session. The student reads this, so it
 * states what they did, not a score from a table they have never seen.
 */
const buildProposeNote = (s) =>
  s ? `I had a look at your ${s.label.toLowerCase()} — ${s.done} questions in and ${s.percent}% of them right. Worth half an hour together on it.` : "";

export default function RecordActionPanel({ open, mode, record, studentId, onClose, onDone, prefill = null, canTeach = true }) {
  const { enqueueSnackbar } = useSnackbar();
  const { school } = useSchool();
  const [busy, setBusy] = useState(false);

  // propose
  const [duration, setDuration] = useState(60);
  const [slots, setSlots] = useState([]);
  const [slotsState, setSlotsState] = useState({ loading: false, error: null, degraded: false, price: null, timezone: null });
  const [chosen, setChosen] = useState(null);
  const [note, setNote] = useState("");
  const [subject, setSubject] = useState("");
  // Fifteen days of identical 09:00–16:00 rows in one scrolling box is not a
  // choice, it is a wall — you cannot tell Tuesday from the Tuesday after it,
  // and the button is three weeks below the fold. One day at a time.
  const [day, setDay] = useState(null);

  // task
  const [taskTitle, setTaskTitle] = useState("");
  const [taskDue, setTaskDue] = useState("");

  /**
   * The weakest subject by REAL answered questions, not by AiLearnProgress.
   * Needs enough behind it to name out loud — 3 wrong out of 4 is not a
   * subject someone is weak at, it is a Tuesday.
   */
  const coverageWeakest = (record?.coverage?.subjects || [])
    .filter((s) => s.done >= 8 && s.percent != null)
    .sort((a, b) => a.percent - b.percent)[0] || null;

  // Pre-fill from where they are actually weakest. An instructor should be
  // agreeing with a suggestion, not composing one from scratch.
  useEffect(() => {
    if (!open) return;
    if (mode === "propose" || mode === "flag") {
      // A SUBJECT KEY, NOT A LESSON TITLE.
      // This field is sent to /my-slots and to propose_booking, and both hand
      // it to resolveSessionRate, which looks it up in the instructor's
      // `rates.bySubject` — a map keyed "air-law", "meteorology". It used to be
      // pre-filled with `weakest.title` ("L1 Air Law"), which matches no key,
      // so an instructor who had set £40 for air law was quoted the default
      // and proposed at that price. Free text could never have matched either.
      setSubject(prefill?.subject || coverageWeakest?.subject || "");
      // `prefill` exists because this panel is opened from two places and only
      // one of them has a record. The pipeline board passes `weakest: []`, so
      // the note came out empty — and propose_session REJECTS an empty note.
      // Proposing from the board was therefore strictly worse than proposing
      // from the record, for instructors too: no pre-fill, and a submit that
      // fails unless you happen to type something first.
      // THIS TEXT IS SHOWN TO THE STUDENT, so the number in it has to be real.
      // It used to come from `weakest`, which is derived from AiLearnProgress —
      // the same dead source that made the header claim "0% covered" for a
      // student with 229 answered questions. It pre-filled "You are at 1% on
      // Air Law and need 75%" for someone actually at 35% by real counts, and
      // propose_session sends that to them.
      //
      // Real counts instead: the weakest subject they have genuinely answered
      // enough of to name.
      setNote(prefill?.note || buildProposeNote(coverageWeakest) || "");
    }
    if (mode === "task") {
      setTaskTitle(prefill?.taskTitle || (coverageWeakest ? `Revise ${coverageWeakest.label}` : ""));
      setTaskDue("");
    }
    if (mode === "note") setNote("");
    // The composer opens with the instructor's own opening line already in it,
    // built from the specific thing the student keeps getting wrong. Editable,
    // and no "AI generated" badge — someone who sends it unedited should feel
    // they wrote it, and someone who changes three words should not feel they
    // overrode a machine.
    if (mode === "message") setNote(prefill?.message || "");
    setChosen(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, mode, prefill?.note, prefill?.subject, prefill?.taskTitle]);

  useEffect(() => {
    if (!open || mode !== "propose") return;
    let cancelled = false;
    setSlotsState((s) => ({ ...s, loading: true, error: null }));
    get_my_slots_api({ duration, subject })
      .then((r) => {
        if (cancelled) return;
        if (r.code === 200) {
          setSlots(r.slots || []);
          setSlotsState({
            loading: false, error: null,
            degraded: r.externalCalendar?.degraded || false,
            price: r.price ?? null, timezone: r.timezone || null,
          });
        } else {
          setSlots([]);
          setSlotsState({ loading: false, error: r.message || "Could not load your availability", degraded: false, price: null, timezone: null });
        }
      })
      .catch(() => {
        if (!cancelled) setSlotsState({ loading: false, error: "Could not load your availability", degraded: false, price: null, timezone: null });
      });
    return () => { cancelled = true; };
  }, [open, mode, duration, subject]);

  const byDay = slots.reduce((acc, s) => {
    const k = moment(s.start_time).format("ddd D MMM");
    (acc[k] = acc[k] || []).push(s);
    return acc;
  }, {});
  const days = Object.keys(byDay);

  // Land on the first day that has anything. Following the slot list rather
  // than setting once matters because changing the duration reloads it, and a
  // day that no longer exists would leave the panel showing no times at all.
  useEffect(() => {
    if (!days.length) { if (day !== null) setDay(null); return; }
    if (!day || !byDay[day]) setDay(days[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slots]);

  const dayList = (day && byDay[day]) || [];
  const morning = dayList.filter((s) => moment(s.start_time).hour() < 12);
  const afternoon = dayList.filter((s) => moment(s.start_time).hour() >= 12);
  const region = regionOf(school);

  const submit = async () => {
    setBusy(true);
    try {
      let r;
      if (mode === "propose") {
        if (!chosen) { enqueueSnackbar("Pick a time first", { variant: "warning" }); setBusy(false); return; }
        if (!note.trim()) { enqueueSnackbar("Tell the student why — they see this", { variant: "warning" }); setBusy(false); return; }
        r = await propose_session_api(studentId, {
          start_time: chosen.start_time, end_time: chosen.end_time, subject, note: note.trim(),
        });
      } else if (mode === "note") {
        r = await add_record_note_api(studentId, { content: note.trim() });
      } else if (mode === "message") {
        if (!note.trim()) { enqueueSnackbar("Write something first", { variant: "warning" }); setBusy(false); return; }
        r = await message_student_api(studentId, { body: note.trim() });
      } else if (mode === "task") {
        r = await create_student_task_api(studentId, {
          title: taskTitle.trim(), body: note.trim(),
          dueAt: taskDue || null,
          // ACS codes are an FAA construct and this panel never had any to
          // send: both branches of the ternary that used to be here were `[]`.
          acsCodes: [],
        });
      } else if (mode === "flag") {
        r = await flag_weak_area_api(studentId, { subject, note: note.trim() });
      }
      if (r && (r.code === 200 || r.code === 201)) {
        // The controller saves the message first and delivers second, and
        // returns `delivered:false` when the email threw. Reporting plain
        // success there would tell an instructor their student had been
        // written to when they had not.
        if (mode === "message" && r.delivered === false) {
          enqueueSnackbar("Saved to the record, but the email didn't go — try again", { variant: "warning" });
        } else if (mode === "propose" && r.notified === false) {
          // The proposal exists and the slot is held either way. What did not
          // happen is the email, and an instructor who thinks their student was
          // told will wait three days for an answer to a message nobody got.
          enqueueSnackbar("Proposed, but the email didn't go — it's waiting for them in the portal", { variant: "warning" });
        } else {
          enqueueSnackbar(
            mode === "propose" ? "Proposed — the student has been asked to confirm"
            : mode === "message" ? `Sent to ${record?.student?.name?.split(" ")[0] || "the student"}`
            : mode === "note" ? "Note saved to the record"
            : mode === "task" ? "Task assigned"
            : "Flagged", { variant: "success" });
        }
        onDone();
        onClose();
      } else {
        enqueueSnackbar(r?.message || "That didn't go through", { variant: "error" });
      }
    } catch {
      enqueueSnackbar("That didn't go through", { variant: "error" });
    } finally {
      setBusy(false);
    }
  };

  const TITLES = {
    propose: "Propose a session",
    // Says plainly that the student receives this — the difference between
    // this and "Add a note" is the whole point, and it must not be guessable
    // only from which button you happened to press.
    message: "Message the student",
    note: "Add a note",
    task: "Assign a task",
    flag: "Flag a weak area",
  };

  return (
    <Drawer anchor="right" open={open} onClose={onClose}
      PaperProps={{ sx: { width: { xs: "100%", sm: 420 }, p: 2, backgroundColor: "#FAF8F5" } }}>
      <Box sx={{ display: "flex", alignItems: "center", mb: 1.5 }}>
        <Typography sx={{ fontFamily: font, fontSize: "15px", fontWeight: 800, color: textPrimary, flex: 1 }}>
          {TITLES[mode]}
        </Typography>
        <IconButton size="small" onClick={onClose}><CloseIcon sx={{ fontSize: 18 }} /></IconButton>
      </Box>

      <Typography sx={{ ...metaSx, fontFamily: font, mb: 1.5 }}>
        {record?.student?.name}
        {/* The old line here read "weakest: L1 Air Law (1%)" and came from
            AiLearnProgress — the same dead source that told the record header
            a student with 229 answered questions was 0% covered. It was a
            second, usually contradictory diagnosis printed above a message
            about a different topic entirely. Real counts, or nothing. */}
        {coverageWeakest && mode !== "message"
          ? ` · weakest: ${coverageWeakest.label}, ${coverageWeakest.percent}% of ${coverageWeakest.done}`
          : ""}
      </Typography>

      {mode === "propose" && (
        <>
          {/* LENGTH AND PRICE ON ONE LINE.
              What it costs is a property of how long it is, so the two belong
              together — and the price has to move when the pills do, or an
              instructor reads last selection's figure. The payment terms sit
              underneath as a sentence. */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 0.75 }}>
            {DURATIONS.map((d) => (
              <Box key={d} onClick={() => { setDuration(d); setChosen(null); }} sx={{
                px: 1.75, py: 0.55, borderRadius: "999px", cursor: "pointer", fontFamily: font,
                fontSize: "12.5px", fontWeight: 700, transition: `all 0.15s ${easing}`,
                color: duration === d ? "#fff" : textSecondary,
                backgroundColor: duration === d ? textPrimary : "#fff",
                border: `1px solid ${duration === d ? textPrimary : border}`,
              }}>{d} min</Box>
            ))}
            {slotsState.price != null && (
              <Typography sx={{
                fontFamily: font, fontSize: "15px", fontWeight: 800, ml: "auto",
                color: textPrimary,
              }}>
                {money(slotsState.price, region)}
              </Typography>
            )}
          </Box>
          <Typography sx={{ fontFamily: font, fontSize: "11.5px", color: textMuted, mb: 1.75 }}>
            They pay when they confirm. Nothing is charged now.
          </Typography>

          {slotsState.degraded && (
            <Typography sx={{ fontFamily: font, fontSize: "11.5px", color: warning, mb: 1 }}>
              Your external calendar could not be read, so these times reflect XAir bookings only.
            </Typography>
          )}

          <Box sx={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", mb: 0.75 }}>
            <Typography sx={{ ...labelSx, fontFamily: font }}>Your free times</Typography>
            {slotsState.timezone && (
              <Typography sx={{ ...metaSx, fontFamily: font }}>{slotsState.timezone}</Typography>
            )}
          </Box>

          {slotsState.loading && <CircularProgress size={16} sx={{ color: accent, mb: 1.5 }} />}
          {slotsState.error && (
            <Typography sx={{ fontFamily: font, fontSize: "12px", color: errorColor, mb: 1.5 }}>{slotsState.error}</Typography>
          )}
          {!slotsState.loading && !slotsState.error && !slots.length && (
            <Typography sx={{ fontFamily: font, fontSize: "12px", color: textSecondary, lineHeight: 1.45, mb: 1.5 }}>
              {canTeach
                ? "You have no free slots in the next three weeks. Set your working hours under My Availability, or clear a block."
                // The endpoint builds slots from the CALLER's working hours,
                // so someone who does not teach gets an empty list and is
                // told to fix their availability — advice for a problem they
                // do not have. They are not short of slots; this is not their
                // action.
                : `This offers times from your own diary, and you are not set up to teach. Close this and use “Ask ${anInstructor(region)}” instead.`}
            </Typography>
          )}

          {days.length > 0 && (
            <>
              {/* Days across, times below. Three weeks of availability is a
                  row you skim, not a column you scroll — and the day holding
                  the picked time keeps a mark on it, so choosing Thursday and
                  then looking at Friday does not read as having chosen
                  nothing. */}
              <Box sx={{
                display: "flex", gap: 0.5, overflowX: "auto", pb: 0.75, mb: 1,
                "&::-webkit-scrollbar": { height: 4 },
                "&::-webkit-scrollbar-thumb": { backgroundColor: border, borderRadius: 2 },
              }}>
                {days.map((d) => {
                  const on = d === day;
                  const holdsChoice = chosen && moment(chosen.start_time).format("ddd D MMM") === d;
                  const [wd, dnum] = d.split(" ");
                  return (
                    <Box key={d} onClick={() => setDay(d)} sx={{
                      flexShrink: 0, position: "relative", textAlign: "center", cursor: "pointer",
                      px: 1.1, py: 0.5, borderRadius: "8px", minWidth: 44,
                      transition: `all 0.12s ${easing}`,
                      backgroundColor: on ? textPrimary : "#fff",
                      border: `1px solid ${on ? textPrimary : border}`,
                    }}>
                      <Typography sx={{
                        fontFamily: font, fontSize: "10px", fontWeight: 700, lineHeight: 1.2,
                        textTransform: "uppercase", letterSpacing: "0.04em",
                        color: on ? "rgba(255,255,255,0.7)" : textMuted,
                      }}>{wd}</Typography>
                      <Typography sx={{
                        fontFamily: font, fontSize: "13px", fontWeight: 700, lineHeight: 1.25,
                        color: on ? "#fff" : textPrimary,
                      }}>{dnum}</Typography>
                      {holdsChoice && (
                        <Box sx={{
                          position: "absolute", bottom: 3, left: "50%", transform: "translateX(-50%)",
                          width: 4, height: 4, borderRadius: "50%",
                          backgroundColor: on ? "#fff" : accent,
                        }} />
                      )}
                    </Box>
                  );
                })}
              </Box>

              <Box sx={{ mb: 1.75 }}>
                {[["Morning", morning], ["Afternoon", afternoon]].map(([heading, list]) => (
                  !list.length ? null : (
                    <Box key={heading} sx={{ mb: 1 }}>
                      {/* The split only earns its keep when there is something
                          on both sides of noon. */}
                      {morning.length > 0 && afternoon.length > 0 && (
                        <Typography sx={{ ...metaSx, fontFamily: font, mb: 0.5 }}>{heading}</Typography>
                      )}
                      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(62px, 1fr))", gap: 0.6 }}>
                        {list.map((s) => {
                          const on = chosen?.start_time === s.start_time;
                          return (
                            <Box key={s.start_time} onClick={() => setChosen(s)} sx={{
                              py: 0.6, borderRadius: "8px", cursor: "pointer", fontFamily: font,
                              fontSize: "12.5px", fontWeight: 600, textAlign: "center",
                              transition: `all 0.12s ${easing}`,
                              color: on ? "#fff" : textPrimary,
                              backgroundColor: on ? accent : "#fff",
                              border: `1px solid ${on ? accent : border}`,
                              "&:hover": { borderColor: on ? accent : textMuted },
                            }}>
                              {moment(s.start_time).format("HH:mm")}
                            </Box>
                          );
                        })}
                      </Box>
                    </Box>
                  )
                ))}
              </Box>
            </>
          )}

          {/* THE SUBJECT IS A KEY, SO IT IS A LIST.
              Typing here decided the price — resolveSessionRate looks the
              value up in the instructor's per-subject rates — and free text
              never matched, so a school's own prices were silently bypassed.
              It is also the field that filters the slots above it. */}
          <TextField
            select SelectProps={{ native: true }} label="Subject" size="small" fullWidth
            value={subject} onChange={(e) => { setSubject(e.target.value); setChosen(null); }}
            InputLabelProps={{ shrink: true }} sx={{ ...inputSx, mb: 1.5 }}
          >
            <option value="">Any subject</option>
            {subjectsForRegion(regionOf(school)).map((s) => (
              <option key={s} value={s}>{formatSubjectName(s)}</option>
            ))}
          </TextField>
        </>
      )}

      {mode === "task" && (
        <>
          <TextField label="Task" size="small" fullWidth value={taskTitle}
            onChange={(e) => setTaskTitle(e.target.value)} sx={{ ...inputSx, mb: 1.5 }} />
          <TextField label="Due" type="date" size="small" fullWidth value={taskDue}
            onChange={(e) => setTaskDue(e.target.value)} InputLabelProps={{ shrink: true }} sx={{ ...inputSx, mb: 1.5 }} />
          <Typography sx={{ ...metaSx, fontFamily: font, mb: 1 }}>
            You wrote this, so it goes straight to the student — no approval step.
          </Typography>
        </>
      )}

      {mode === "flag" && (
        <TextField label="Subject" size="small" fullWidth value={subject}
          onChange={(e) => setSubject(e.target.value)} sx={{ ...inputSx, mb: 1.5 }} />
      )}

      <TextField
        label={mode === "propose" ? "Why (the student sees this)"
          : mode === "message" ? "Your message (the student receives this)"
          : mode === "flag" ? "What you saw"
          : mode === "task" ? "Detail (optional)" : "Note"}
        size="small" fullWidth multiline minRows={3}
        value={note} onChange={(e) => setNote(e.target.value)} sx={{ ...inputSx, mb: 1.5 }}
      />

      {mode === "propose" && (
        <Typography sx={{ ...metaSx, fontFamily: font, mb: 1.5, lineHeight: 1.45 }}>
          It seeds the pre-session agenda and stays on the record.
        </Typography>
      )}

      {/* WHAT YOU ARE ABOUT TO SEND, WHERE YOU SEND IT.
          The time was picked several hundred pixels up, past a subject field
          and a paragraph of terms. Restating it against the button is the
          difference between confirming an offer and hoping. */}
      <Box sx={{
        mt: "auto", pt: 1.5, position: "sticky", bottom: 0,
        backgroundColor: "#FAF8F5", borderTop: `1px solid ${border}`,
      }}>
        {mode === "propose" && (
          <Typography sx={{
            fontFamily: font, fontSize: "12.5px", mb: 1, lineHeight: 1.45,
            fontWeight: chosen ? 700 : 400,
            color: chosen ? textPrimary : textMuted,
          }}>
            {chosen
              ? `${moment(chosen.start_time).format("ddd D MMM")} · ${moment(chosen.start_time).format("HH:mm")}–${moment(chosen.end_time).format("HH:mm")}${
                slotsState.price != null ?` · ${money(slotsState.price, region)}` : ""}`
              : "Pick a time above to send this."}
          </Typography>
        )}
        <Box sx={{ display: "flex", gap: 1, pb: 0.5 }}>
          <Button onClick={submit} disabled={busy} sx={{ ...primaryButtonSx, flex: 1 }}>
            {busy ? "Working…" : mode === "propose" ? "Send proposal" : mode === "message" ? "Send message" : mode === "note" ? "Save note" : mode === "task" ? "Assign" : "Flag"}
          </Button>
          <Button onClick={onClose} sx={secondaryButtonSx}>Cancel</Button>
        </Box>
      </Box>
    </Drawer>
  );
}
